import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { PageHero, Section, Btn, ArrowLink, SectionHead, Accordion } from "@/components/kit";
import { Reveal, Eyebrow } from "@/lib/anim";
import { OfferGrid } from "@/components/Offer";
import { PRICING, STRATEGY, RETAINER, TRANSPARENCY, FAQ_GROUPS } from "@/lib/content";

const STEPS = [
  ["01", "Free intro call", "20 minutes, no charge. Understand who Prox is, how it works, and whether it can help. No pressure."],
  ["02", "Strategy call", "A paid 60 or 90 minute working session on your biggest product, cost or sourcing question."],
  ["03", "Engagements & retainers", "The larger, hands-on work for brands that want Prox to build the plan or run it with them."],
];

export default function Pricing() {
  const engagements = PRICING.filter((p) => p.id !== "fit_call");
  const moneyFaq = FAQ_GROUPS.find((g) => g.title === "Money");
  const faqJsonLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: moneyFaq.items.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  };
  return (
    <>
      <Seo title="Pricing" canonical="/pricing" description="Prox pricing, in AUD inclusive of GST. Start with a free 20-minute call, step up to a paid strategy call, then engagements and retainers as the work needs." jsonLd={faqJsonLd} />
      <PageHero
        eyebrow="Pricing"
        crumbs={[{ label: "Home", to: "/" }, { label: "Pricing" }]}
        title="Start free. Step up only as far as the work needs."
        lead="Every price is published, in Australian dollars and inclusive of GST. A clear, low-risk path: a free call, then a paid strategy call, then larger engagements if and when they make sense."
      />

      {/* The ladder */}
      <Section bg="charcoal" dark pad={false} className="py-14 md:py-16">
        <div className="grid md:grid-cols-3 gap-8">
          {STEPS.map((s, i) => (
            <Reveal i={i} key={s[0]}>
              <div>
                <div className="font-mono text-[var(--clay)]">{s[0]}</div>
                <h3 className="t-h4 text-[var(--bone)] mt-3">{s[1]}</h3>
                <p className="t-sm text-[var(--muted-dark)] mt-3">{s[2]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Step 1: Free call */}
      <Section bg="bone">
        <SectionHead eyebrow="Step 1 · Free" title="The intro call." lead="Twenty minutes on video to see if Prox is the right fit. Always free." />
        <div className="card p-8 md:p-10 mt-10 flex flex-col md:flex-row md:items-center justify-between gap-8" data-testid="pricing-free-call">
          <div className="max-w-2xl">
            <div className="flex items-baseline gap-4">
              <h3 className="t-h3">Intro Call</h3>
              <span className="font-mono text-[var(--clay-deep)] text-lg">Free · 20 min</span>
            </div>
            <p className="t-body text-[var(--muted)] mt-4">A straight answer on whether Prox can help, which piece of work fits, and what it will cost. No deck, no follow-up sequence.</p>
          </div>
          <Btn to="/book" variant="clay" data-testid="pricing-book-free">Book the free call</Btn>
        </div>
      </Section>

      {/* Step 2: Strategy calls */}
      <Section bg="sand">
        <SectionHead eyebrow="Step 2 · Paid strategy call" title="Get senior input on one real decision." lead="Pay by card, then pick a time. Choose the length that fits the problem." />
        <div className="mt-12"><OfferGrid items={STRATEGY} full /></div>
      </Section>

      {/* Step 3: Engagements */}
      <Section bg="bone">
        <SectionHead eyebrow="Step 3 · Engagements" title="When you want Prox to do the work." lead="Defined, fixed-price pieces of work for founders and growing brands." />
        <div className="mt-12"><OfferGrid items={engagements} full /></div>
      </Section>

      {/* Retainer */}
      <Section bg="charcoalDeep" dark>
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Eyebrow>Ongoing</Eyebrow>
            <h2 className="t-h2 text-[var(--bone)]">{RETAINER.name}</h2>
            <p className="font-mono text-[var(--clay)] mt-3">{RETAINER.headline}</p>
            <p className="t-sm text-[var(--muted-dark)] mt-6">{RETAINER.note}</p>
          </div>
          <div className="lg:col-span-8 grid md:grid-cols-2 gap-5">
            {RETAINER.tiers.map((t) => (
              <div key={t.name} className="border border-white/12 p-7" style={{ borderRadius: 2 }}>
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="t-h3 text-[var(--bone)]">{t.name}</h3>
                  <span className="font-mono text-[var(--clay)] whitespace-nowrap">{t.price}</span>
                </div>
                <p className="t-sm text-[var(--stone)] mt-4">{t.get}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Transparency */}
      <Section bg="paper">
        <div className="max-w-3xl">
          <Eyebrow>How Prox is paid</Eyebrow>
          <p className="t-lead">{TRANSPARENCY}</p>
        </div>
      </Section>

      {/* Money FAQ */}
      <Section bg="bone">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><SectionHead eyebrow="Money" title="The questions about fees." /><ArrowLink to="/faq" className="mt-6">Full FAQ</ArrowLink></div>
          <div className="lg:col-span-8"><Accordion items={moneyFaq.items.map(([q, a]) => ({ q, a }))} testid="pricing-faq" /></div>
        </div>
      </Section>

      <Section bg="clay" dark className="py-20 md:py-24">
        <div className="max-w-3xl">
          <h2 className="t-display2">Start with the free call.</h2>
          <p className="t-lead mt-6" style={{ color: "rgba(255,255,255,0.85)" }}>Twenty minutes to work out which step fits, and what it will cost.</p>
          <Btn to="/book" variant="bone" className="mt-9">Book the free call</Btn>
        </div>
      </Section>
    </>
  );
}
